// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { CustomEvent } from '@shared/utils/CustomEvents';
import { ErrorInformationStorage } from '@shared/commons/ErrorInformationStorage';
import { TimeSpanPeriods } from '@shared/utils/Time/TimeSpan';
import { TerceraChartCashItemSeriesCacheScreenDataDateChangeType, TerceraChartCashItemSeriesDataTypeBasisType, TerceraChartCashItemSeriesGetIndexType } from '../Series/TerceraChartCashItemSeriesEnums';
import { ChartDataType } from '../Utils/ChartConstants';
import { Periods } from '@shared/utils/History/TFInfo';
import { BaseInterval } from '@shared/utils/History/BaseInterval';
import { DateTimeConvertor } from '@shared/utils/Time/DateTimeConvertor';
import { SessionPeriod } from '@shared/utils/Session/SessionPeriod';
import { getWeek } from 'date-fns/getWeek';
import { BarIndex } from '@shared/commons/cache/History/HistoryConstants';
import { MathUtils } from '@shared/utils/MathUtils';
/// <summary>
/// Серия данных на основе кешитема (аналог DataExtender)
/// Назначение:
/// - индексация баров с учётом опции "Показывать дырки"; RecalcIndices(), FIndexList
/// - кеширование данных, отображаемых в текущий момент на экране. CacheScreenData()
/// </summary>
export class TerceraChartCashItemSeries {
    constructor(newCashItem, settings) {
        this._cashItem = null;
        this._TicksInOneBar = 1;
        this.FIndexList = [];
        /// <summary>
        /// Расчитанное время для дырок
        /// </summary>
        this.ZeroTimes = [];
        /// <summary>
        /// Временные границы данных. (UTC)
        /// </summary>
        this.From = null;
        this.To = null;
        /// <summary>
        /// Вспомогательные
        /// </summary>
        this._DayBasedData = false;
        this.NonLinearTimeline = false;
        this.RelativePeriodBar = 1;
        this._BarDuration = 1;
        this.LeftRelativeBorder = 0;
        this.RightRelativeBorder = 0;
        this.RealLeftRelativeBorderForPaddingBars = 0;
        this.RealRightRelativeBorderForPaddingBars = 0;
        this.IsCustomRange = false;
        this.dataBlocks = []; // List<TerceraChartCashItemSeriesDataBlock>();
        /// <summary>
        /// Кешированные данные по текущему отображаемому экрану
        /// </summary>
        this.ChartScreenData = new TerceraChartCashItemSeriesCacheScreenDataStorage();
        this.settings = null;
        this.SyncronizedSessionFlag = null; // #100139
        this.HistoryExpanded = new CustomEvent();
        this.leftPaddingBarsCount = 0;
        this.rightPaddingBarsCount = 0;
        this.settings = settings;
        this.From = new Date(newCashItem.StartTime);
        this.To = new Date(newCashItem.GetCloseTime(newCashItem.FNonEmptyCashArrayCount - 1));
        this.CashItem = newCashItem;
    }
    get CashItem() {
        return this._cashItem;
    }
    set CashItem(value) {
        // unsubscribe
        if (this._cashItem != null) {
            this._cashItem.HistoryExpanded.UnSubscribe(this.value_HistoryExpanded, this);
            this._cashItem.HistoryReload.UnSubscribe(this.cashItem_HistoryReload, this);
            this._cashItem.QuoteProcessed.UnSubscribe(this.cashItem_QuoteProcessed, this);
        }
        this._cashItem = value;
        // subscribe
        if (this._cashItem != null) {
            this._cashItem.HistoryExpanded.Subscribe(this.value_HistoryExpanded, this);
            this._cashItem.HistoryReload.Subscribe(this.cashItem_HistoryReload, this);
            this._cashItem.QuoteProcessed.Subscribe(this.cashItem_QuoteProcessed, this);
        }
        if (this._cashItem != null) {
            this.RelativePeriodBar = BaseInterval.GetIntervalLength(this._cashItem.FPeriod);
            this._BarDuration = this.GetBarDuration(this._cashItem);
            this.NonLinearTimeline =
                this.RelativePeriodBar === 0 ||
                    this._cashItem.ChartDataType !== ChartDataType.Default ||
                    this._cashItem.FPeriod % Periods.RANGE === 0;
            this._DayBasedData =
                !this.NonLinearTimeline &&
                    this._cashItem.FPeriod >= Periods.DAY &&
                    this._cashItem.FPeriod % Periods.SECOND !== 0;
            //
            this.RecalcIndices();
        }
        if (this._cashItem == null || this._cashItem.FPeriod === Periods.TIC) {
            this._TicksInOneBar = 1;
        }
        else {
            this._TicksInOneBar = value.FPeriod * 60 * TimeSpanPeriods.TicksPerSecond;
        }
    }
    // #region CashItem events
    value_HistoryExpanded(cashItem, oldLength, newLength) {
        this.RecalcIndices();
        this.HistoryExpanded.Raise(true);
    }
    cashItem_HistoryReload(sender) {
        this.RecalcIndices();
    }
    cashItem_QuoteProcessed(cashItem, quote) {
        this.HistoryExpanded.Raise(false);
    }
    // #endregion
    Count() {
        return this.FIndexList.length;
    }
    RecalcIndices() {
        try {
            //
            // Свои расчёты
            //
            // if (cashItem is CashItemFile)
            // {
            //    RecalcIndicesForFileCashItem();
            //    return;
            // }
            const bufferList = []; // new List<int>();
            const bufferZero = []; // new List<long>();
            bufferZero.push(0);
            const bufferBlocks = [];
            let leftBorder = 0;
            let rightBorder = 0;
            const maxBarsCount = TerceraChartCashItemSeries.GetMaxZeroBars(this._cashItem);
            const startTicks = this.From.getTime();
            const endTicks = this.To.getTime();
            /// /
            /// / Понадобилось в алголстудии, подумть
            /// / корректируем date-интервал
            /// /
            // Utils.ProcessDateInterval(ref startTicks, ref endTicks, cashItem.Symbol, cashItem, null, false, false);
            const bi = this._cashItem.GetInterval(0);
            const barLength = bi != null ? bi.FRightTimeTicks - bi.FLeftTimeTicks : 0;
            let curBlock = null;
            let addedIndex = 0;
            const period = this._cashItem.FPeriod;
            const leftPaddingBarsCount = this.leftPaddingBarsCount;
            addedIndex = this.insertFakeBlock(leftPaddingBarsCount, FAKE_LEFT_PADDING_BAR_INDEX, addedIndex, bufferBlocks, bufferList);
            //
            // Показывать дырки
            //
            if (this.settings.ShowEmptyBars) {
                // #region
                let prevBi = null;
                const array = this._cashItem.FNonEmptyCashArray;
                let i;
                const len = array.length;
                for (i = 0; i < len; i++) {
                    const curBi = array[i];
                    // Проверка верменного диапазона
                    if (curBi.FLeftTimeTicks < startTicks || (curBi.FLeftTimeTicks >= endTicks && this.IsCustomRange)) {
                        continue;
                    }
                    // Remember time borders
                    if (leftBorder === 0) {
                        leftBorder = curBi.FLeftTimeTicks;
                    }
                    rightBorder = curBi.FRightTimeTicks;
                    // Дырка?
                    if (prevBi != null && prevBi.FRightTimeTicks < curBi.FLeftTimeTicks) {
                        const zeroes = prevBi.CalculateNextInterv(curBi.LeftTime, 0, period);
                        // Слишком большие дырки не добавляем
                        const controlBigHole = true;
                        if (zeroes < maxBarsCount || !controlBigHole) {
                            if (zeroes > 0) {
                                curBlock = new TerceraChartCashItemSeriesDataBlock(addedIndex, curBi.FLeftTimeTicks);
                                bufferBlocks.push(curBlock);
                                let j;
                                for (j = 0; j < zeroes; j++) {
                                    const zeroBarTime = prevBi.FRightTimeTicks + j * barLength;
                                    bufferZero.push(zeroBarTime);
                                    bufferList.push((bufferZero.length - 1) * -1);
                                    addedIndex++;
                                }
                                curBlock = null;
                            }
                            else if (this._DayBasedData) {
                                // Create block for hole
                                curBlock = new TerceraChartCashItemSeriesDataBlock(addedIndex - 1, prevBi.FRightTimeTicks);
                                curBlock.IsHole = true;
                                bufferBlocks.push(curBlock);
                                curBlock = null;
                            }
                        }
                        else {
                            //
                            // http://tp.pfsoft.lan/Project/QA/Bug/View.aspx?BugID=19540&acid=74FA48A38CBEE17F6445F76C9BEC721B ???
                            //
                            // Create block for hole
                            // curBlock = new DataBlock(addedIndex-1, prevBi.FRightTimeTicks);
                            curBlock = new TerceraChartCashItemSeriesDataBlock(addedIndex, prevBi.FRightTimeTicks);
                            curBlock.IsHole = true;
                            bufferBlocks.push(curBlock);
                            curBlock = null;
                        }
                    }
                    else if (this._DayBasedData && prevBi != null && prevBi.FRightTimeTicks > curBi.FLeftTimeTicks) {
                        // В нахлёст бары лежат - начинаем новый блок
                        curBlock = null;
                    }
                    // Create new block for data
                    if (curBlock == null) {
                        curBlock = new TerceraChartCashItemSeriesDataBlock(addedIndex, curBi.FLeftTimeTicks);
                        bufferBlocks.push(curBlock);
                    }
                    bufferList.push(i);
                    addedIndex++;
                    prevBi = curBi;
                }
                // #endregion
            }
            else {
                // Без дырок
                let prevBi = null;
                const array = this._cashItem.FNonEmptyCashArray;
                const len = array.length;
                for (let i = 0; i < len; i++) {
                    const curBi = array[i];
                    // Проверка верменного диапазона
                    // if (curBi.FLeftTimeTicks < startTicks || (curBi.FLeftTimeTicks >= endTicks && this.IsCustomRange))
                    //    continue;
                    // Remember time borders
                    if (leftBorder === 0) {
                        leftBorder = curBi.FLeftTimeTicks;
                    }
                    rightBorder = curBi.FRightTimeTicks;
                    // Дырка?
                    if (prevBi != null &&
                        prevBi.FRightTimeTicks < curBi.FLeftTimeTicks &&
                        this.RelativePeriodBar !== 0) {
                        // Create block for hole
                        curBlock = new TerceraChartCashItemSeriesDataBlock(addedIndex - 1, prevBi.FRightTimeTicks);
                        curBlock.IsHole = true;
                        bufferBlocks.push(curBlock);
                        curBlock = null;
                    }
                    else if (this._DayBasedData && prevBi != null && prevBi.FRightTimeTicks > curBi.FLeftTimeTicks) {
                        // В нахлёст бары лежат - начинаем новый блок
                        curBlock = null;
                    }
                    // Create new block for data
                    if (curBlock == null) {
                        curBlock = new TerceraChartCashItemSeriesDataBlock(addedIndex, curBi.FLeftTimeTicks);
                        bufferBlocks.push(curBlock);
                    }
                    bufferList.push(i);
                    addedIndex++;
                    prevBi = curBi;
                }
            }
            const rightPaddingBarsCount = this.rightPaddingBarsCount;
            addedIndex = this.insertFakeBlock(rightPaddingBarsCount, FAKE_RIGHT_PADDING_BAR_INDEX, addedIndex, bufferBlocks, bufferList);
            if (leftPaddingBarsCount > 0) {
                this.RealLeftRelativeBorderForPaddingBars = leftBorder;
                const fakeTimeDuration = leftPaddingBarsCount * this._BarDuration;
                const fakeLeftBorder = leftBorder - fakeTimeDuration;
                bufferBlocks[0].LeftTime = fakeLeftBorder;
                leftBorder = fakeLeftBorder;
            }
            if (rightPaddingBarsCount > 0) {
                this.RealRightRelativeBorderForPaddingBars = rightBorder;
                const fakeTimeDuration = rightPaddingBarsCount * this._BarDuration;
                const fakeRightBorder = rightBorder + fakeTimeDuration;
                bufferBlocks.at(-1).LeftTime = this.RealRightRelativeBorderForPaddingBars;
                rightBorder = fakeRightBorder;
            }
            //
            // Сначала во временный массив, иначе лажает иногда на промежуточных этапах
            //
            this.FIndexList = bufferList;
            this.ZeroTimes = bufferZero;
            this.LeftRelativeBorder = leftBorder;
            this.RightRelativeBorder = rightBorder;
            this.dataBlocks = bufferBlocks;
        }
        catch (ex) {
            ErrorInformationStorage.GetException(ex);
        }
    }
    insertFakeBlock(paddingBarsCount, fakeBarIndex, addedIndex, bufferBlocks, bufferList) {
        if (paddingBarsCount < 1) {
            return addedIndex;
        }
        const fakeBlock = new TerceraChartCashItemSeriesDataBlock(addedIndex, 0);
        bufferBlocks.push(fakeBlock);
        for (let i = 0; i < paddingBarsCount; i++) {
            bufferList.push(fakeBarIndex);
        }
        addedIndex += paddingBarsCount;
        return addedIndex;
    }
    GetValue(index, level) {
        let res = 0;
        if (this._cashItem === null) {
            return res;
        }
        //
        // Обращение ко времени - дополнительная логика
        // для дырок, прошлого и будущего
        //
        if (level === BarIndex.Time || level === BarIndex.TimeClose) {
            const length = this.FIndexList.length;
            // если это доступ к времени, то вычисляем время бара в будущем
            if (index < 0) {
                res = 0.0;
            }
            else if (index >= 0 && index < length) {
                index = this.GetIndex(index);
                if (index < 0) {
                    index *= -1;
                    return index < this.ZeroTimes.length ? this.ZeroTimes[index] : 0;
                }
                else {
                    res = this._cashItem.GetByConst(index, level);
                }
            }
            else {
                const lastIndex = this.GetIndex(length - 1);
                res = this._cashItem.GetByConst(lastIndex, level) + this.GetTime(index - length + 1);
            }
        }
        else {
            // Price
            index = this.GetIndex(index);
            if (index < 0) {
                res = 0;
            }
            else {
                res = this._cashItem.GetByConst(index, level);
                if (level !== BarIndex.Volume) {
                    res = this.settings.CalculateConverter(res);
                }
            }
        }
        return res;
    }
    GetFormatValueConverter(index, level, instrument) {
        const value = this.GetValue(index, level);
        return this.settings.FormatPriceConverter(value, instrument);
    }
    /// <summary>
    /// Получить реальный индекс в кеш итеме по индексу екстендера
    /// !!! Может быть отрицательным - значит там дырка!
    /// </summary>
    GetIndex(index, getIndexType = TerceraChartCashItemSeriesGetIndexType.Exactly) {
        if (getIndexType === TerceraChartCashItemSeriesGetIndexType.Exactly) {
            return index >= 0 && index < this.FIndexList.length ? this.FIndexList[index] : -1;
        }
        else {
            // Incorrect case
            if (index < 0 || index >= this.FIndexList.length) {
                return -1;
            }
            let res = this.FIndexList[index];
            if (res < 0) {
                while (res < 0) {
                    index++;
                    if (index >= this.FIndexList.length) {
                        // не нашли в итоге
                        return -1;
                    }
                    res = this.FIndexList[index];
                }
                return res;
            }
            else {
                // bar is not empty
                return res;
            }
        }
    }
    /// <summary>
    /// Кешировать данные для указаного промежутка (обычно видимые на экране)
    /// </summary>
    CacheScreenData(start, end, ins) {
        const _cashItem = this._cashItem;
        const period = _cashItem ? _cashItem.FPeriod : Periods.MIN;
        const dataType = _cashItem ? _cashItem.ChartDataType : ChartDataType.Default;
        // var info = (period == Periods.TIC) ? new VolumeInfo() : null;
        const tradingDaySessionStartLocalMillis = ins.Session.CurrentSessionPeriod
            ? ins.Session.ServerBeginDayDate.getTime()
            : null;
        const tempChartScreenData = new TerceraChartCashItemSeriesCacheScreenDataStorage();
        // optimization: avoid DateTime.Get...() functions
        let lastCs = new TerceraChartCashItemSeriesCacheScreenData();
        const utcMillis = this.GetValue(start > 0 ? start - 1 : start, BarIndex.Time);
        lastCs.DateTime = DateTimeConvertor.getLocalTimeQuick(utcMillis);
        lastCs.Year = lastCs.DateTime.getFullYear();
        lastCs.Day = lastCs.DateTime.getDate();
        lastCs.Hour = lastCs.DateTime.getHours();
        lastCs.Month = lastCs.DateTime.getMonth();
        // var lastDateTime = new Date(this.GetValue(start > 0 ? start - 1 : start, BarIndex.Time));
        /// /
        /// / Обновлять надо до основного цикла, данные зависятот базового значения
        /// /
        this.UpdateBasisValue(start, ins);
        const from = Math.max(0, start);
        end += this.leftPaddingBarsCount;
        // var subItemStartIndex = ProfileFindInterval(from);
        //    var customInstrSessions = false;
        //    var revertedSession = false; // значит начало > конца (например с 20:00 до 5:00)
        //    TimeSpan ts1 = TimeSpan.Zero, ts2 = TimeSpan.Zero;
        //    if (ins is InstrumentHistory && ((InstrumentHistory)ins).IsSessionTimeSettings)
        // {
        //                ts1 = ((InstrumentHistory)ins).BeginDayDate;
        //    ts2 = ((InstrumentHistory)ins).EndDayDate;
        //    customInstrSessions = ts1 != ts2;
        //    revertedSession = ts1 > ts2;
        // }
        for (let i = from; i <= end; i++) {
            const cs = new TerceraChartCashItemSeriesCacheScreenData();
            // IsMain session
            const biIndex = this.GetIndex(i);
            cs.Time = this.GetValue(i, BarIndex.Time);
            cs.BaseIntervalIndex = biIndex;
            if (i < 0 ||
                biIndex < 0 ||
                biIndex === FAKE_RIGHT_PADDING_BAR_INDEX ||
                biIndex === FAKE_LEFT_PADDING_BAR_INDEX) {
                cs.Hole = true;
            }
            // Add correct time for holes
            if (i < 0 || biIndex === FAKE_LEFT_PADDING_BAR_INDEX) {
                cs.Time = this.LeftRelativeBorder + i * this._BarDuration;
            }
            else if (i >= this.FIndexList.length || biIndex === FAKE_RIGHT_PADDING_BAR_INDEX) {
                cs.Time = this.RightRelativeBorder + (i - this.FIndexList.length) * this._BarDuration;
            }
            if (period === Periods.TIC && dataType === ChartDataType.Default) {
                cs.Open = this.GetValue(i, BarIndex.Open);
                cs.Close = this.GetValue(i, BarIndex.Close);
                cs.Volume = this.GetValue(i, BarIndex.Volume);
            }
            else {
                cs.Open = this.GetValue(i, BarIndex.Open);
                cs.High = this.GetValue(i, BarIndex.High);
                cs.Low = this.GetValue(i, BarIndex.Low);
                cs.Close = this.GetValue(i, BarIndex.Close);
                cs.Volume = this.GetValue(i, BarIndex.Volume);
            }
            cs.DateTime = DateTimeConvertor.getLocalTimeQuick(cs.Time);
            cs.Year = cs.DateTime.getFullYear();
            cs.Day = cs.DateTime.getDate();
            cs.Hour = cs.DateTime.getHours();
            cs.Month = cs.DateTime.getMonth();
            if (ins != null) {
                cs.DateTimeInSessionTimeZoneWithOffset = Periods.ConvertToDisplayedTimeForDayAggregations(new Date(cs.Time), ins, period);
            }
            else {
                cs.DateTimeInSessionTimeZoneWithOffset = cs.DateTime;
            }
            //
            // Control min/max
            //
            let low = cs.Low;
            let high = cs.High;
            const isNotHole = !cs.Hole;
            if (period === Periods.TIC) {
                low = Math.min(cs.Open, cs.Close);
                high = Math.max(cs.Open, cs.Close);
            }
            const dayHigh = this.settings.CalculateConverter(ins.GetHigh(_cashItem.HistoryType));
            const dayLow = this.settings.CalculateConverter(ins.GetLow(_cashItem.HistoryType));
            if (isNotHole && MathUtils.equalToEpsilon(low, dayLow) && cs.Time >= tradingDaySessionStartLocalMillis) {
                tempChartScreenData.MinDayLowIndex = i - from;
            }
            if (isNotHole && MathUtils.equalToEpsilon(high, dayHigh) && cs.Time >= tradingDaySessionStartLocalMillis) {
                tempChartScreenData.MaxDayHighIndex = i - from;
            }
            if (isNotHole && low < tempChartScreenData.MinLow) {
                tempChartScreenData.MinLow = low;
            }
            if (isNotHole && high > tempChartScreenData.MaxHigh) {
                tempChartScreenData.MaxHigh = high;
            }
            //
            // Change day/month/year
            let YearFlag = false;
            let MonthFlag = false;
            let WeekFlag = false;
            let daySeparate = cs.Separator;
            const isRange = period % Periods.RANGE === 0;
            // сервер сейчас криво даёт эту инфу, сами пока
            daySeparate = lastCs.Day !== cs.Day;
            // Days separator
            if (period <= Periods.DAY || isRange) {
                // для 1 минуты присылает сервер признак, иначе определяем самостоятельно
                if (period !== Periods.MIN) {
                    daySeparate = lastCs.Day !== cs.Day;
                }
                if (daySeparate) {
                    cs.DateChangeType = TerceraChartCashItemSeriesCacheScreenDataDateChangeType.Day;
                }
            }
            //  Week separator
            if (period < Periods.DAY || isRange) {
                const changeWeek = getWeek(lastCs.DateTime, { weekStartsOn: 1 }) !== getWeek(cs.DateTime, { weekStartsOn: 1 });
                if (changeWeek) {
                    WeekFlag = true;
                }
                // set flags
                if (WeekFlag && daySeparate) {
                    WeekFlag = false;
                    cs.DateChangeType = TerceraChartCashItemSeriesCacheScreenDataDateChangeType.Week;
                }
            }
            // Month separator
            if (period < Periods.MONTH) {
                const changeMonth = lastCs.Month !== cs.Month;
                if (changeMonth) {
                    MonthFlag = true;
                }
                // set flags
                if (MonthFlag && daySeparate) {
                    MonthFlag = false;
                    cs.DateChangeType = TerceraChartCashItemSeriesCacheScreenDataDateChangeType.Month;
                }
            }
            // year separator
            if (period < Periods.YEAR) {
                const changeYear = lastCs.Year !== cs.Year;
                if (changeYear) {
                    YearFlag = true;
                }
                // set flags
                if (YearFlag /* && daySeparate */) {
                    YearFlag = false;
                    cs.DateChangeType = TerceraChartCashItemSeriesCacheScreenDataDateChangeType.Year;
                }
            }
            const bbi = _cashItem.GetInterval(biIndex);
            if (bbi) {
                cs.IsMainSession = SessionPeriod.IsMainType(bbi.SessionType) || period >= Periods.DAY;
                if (!cs.IsMainSession && !this.settings.chartMVCModel.GetShowAllSessions()) {
                    cs.NotShowExtendedSession = true;
                }
            }
            lastCs = cs;
            tempChartScreenData.Storage.push(cs);
        }
        // this.SyncronizedSessionFlag = _cashItem.SyncronizedSessionFlag;
        // Replace
        this.ChartScreenData = tempChartScreenData;
    }
    UpdateBasisValue(startIndexBar, instrument) {
        var _a;
        if (this.settings == null) {
            return;
        }
        let newBasisValue = 0;
        const indexOfBeginDataBar = (_a = this.FIndexList.find((i) => i >= 0)) !== null && _a !== void 0 ? _a : -1;
        switch (this.settings.BasisType) {
            case TerceraChartCashItemSeriesDataTypeBasisType.BeginOfScreen:
                if (startIndexBar >= 0) {
                    startIndexBar = this.GetIndex(startIndexBar + 1, TerceraChartCashItemSeriesGetIndexType.ExactlyOrNearestNonEmpty);
                    if (startIndexBar < 0) {
                        // ?
                    }
                    else {
                        newBasisValue = this._cashItem.GetByConst(startIndexBar, BarIndex.Open);
                    }
                }
                else if (indexOfBeginDataBar !== -1) {
                    newBasisValue = this._cashItem.GetByConst(indexOfBeginDataBar, BarIndex.Open);
                }
                break;
            case TerceraChartCashItemSeriesDataTypeBasisType.BeginOfDay:
                if (!isNullOrUndefined(instrument)) {
                    newBasisValue = instrument.Level1.GetPrevClose();
                    if (isNaN(newBasisValue)) {
                        newBasisValue = instrument.Level1.GetOpen();
                    }
                    if (isNaN(newBasisValue)) {
                        const indexOfCloseDataBar = this.Count() - this.rightPaddingBarsCount - 1;
                        newBasisValue = this._cashItem.GetByConst(indexOfCloseDataBar, BarIndex.Close);
                    }
                }
                break;
        }
        this.settings.logDataConverter.BasisValue = this.settings.relativeDataConverter.BasisValue = newBasisValue;
    }
    Dispose() {
        // var volumeCache = GetVolumeCache();
        // if (volumeCache != null)
        // {
        //    volumeCache.RemoveListener(this, cashItem);
        //    if (profileSubItem != null)
        //        volumeCache.RemoveListener(this, profileSubItem);
        // }
        // profileSubItem = null;
        if (this._cashItem) {
            this._cashItem.Dispose();
        }
        this.CashItem = null;
        this.settings = null;
    }
    // public void DisableCashItemEvents()
    // {
    //    if (cashItem != null)
    //    {
    //        cashItem.HistoryExpanded -= value_HistoryExpanded;
    //        cashItem.HistoryReload -= cashItem_HistoryReload;
    //        cashItem.QuoteProcessed -= cashItem_QuoteProcessed;
    //    }
    // }
    GetTimePeriodCount(period, count) {
        if (period < Periods.TIC) {
            return count * -period * TimeSpanPeriods.TicksPerSecond;
        }
        else if (period === Periods.TIC || period % Periods.SECOND === 0) {
            return count * TimeSpanPeriods.TicksPerSecond;
        }
        else {
            return count * period * TimeSpanPeriods.TicksPerMinute;
        }
    }
    GetTime(count) {
        return this.GetTimePeriodCount(this._cashItem.FPeriod, count);
    }
    GetTimeLTRT(index, refLtRt) {
        // Получаем нерасширенную длину истории
        const length = this.FIndexList.length; // +++напрямую получаем
        // если это доступ к времени, то вычисляем время бара в будущем
        if (index < 0) {
            refLtRt.lt = 0;
            refLtRt.rt = 0;
        }
        else if (index >= 0 && index < length) {
            const realIndex = index;
            index = this.GetIndex(index);
            if (index === FAKE_LEFT_PADDING_BAR_INDEX || index === FAKE_RIGHT_PADDING_BAR_INDEX) {
                this.GetPaddingTimeLTRT(realIndex, refLtRt);
            }
            else if (index < 0) {
                refLtRt.lt = this.ZeroTimes[index * -1];
                refLtRt.rt = refLtRt.lt + this.GetTime(1);
            }
            else {
                refLtRt.lt = this._cashItem.GetOpenTime(index);
                refLtRt.rt = this._cashItem.GetCloseTime(index);
            }
        }
        else {
            const lastIndex = this.GetIndex(length - 1);
            refLtRt.lt = this._cashItem.GetOpenTime(lastIndex) + this.GetTime(index - length + 1);
            refLtRt.rt = refLtRt.lt + this.GetTime(1);
        }
    }
    GetPaddingTimeLTRT(realIndex, refLtRt) {
        if (this.leftPaddingBarsCount > 0) {
            const realfirstBarLeftTime = this.RealLeftRelativeBorderForPaddingBars;
            const fakePaddingLeftTime = realfirstBarLeftTime + (realIndex - this.leftPaddingBarsCount) * this._BarDuration;
            refLtRt.lt = fakePaddingLeftTime;
            refLtRt.rt = fakePaddingLeftTime + this._BarDuration;
        }
        else if (this.rightPaddingBarsCount > 0) {
            const rightTimeBorder = this.RightRelativeBorder;
            const fakePaddingRightTime = rightTimeBorder - (realIndex - this.rightPaddingBarsCount) * this._BarDuration;
            refLtRt.lt = fakePaddingRightTime - this._BarDuration;
            refLtRt.rt = fakePaddingRightTime;
        }
        else {
            refLtRt.lt = this.LeftRelativeBorder;
            refLtRt.rt = this.LeftRelativeBorder + this._BarDuration;
        }
    }
    /// // <summary>
    /// // Поиск индекса бара по времени.
    /// // Старый способ - бинарный поиск, используем для нелинейных шкал
    /// // </summary>
    FindIntervalBinary(time) {
        // Оптимизируем
        let to = this.FIndexList.length - 1;
        let from = 0;
        let middle = 0;
        const refLtRt = { lt: 0, rt: 0 };
        // Бинарный поиск
        while (to - from > 1) {
            middle = Math.floor(from + (to - from) / 2);
            this.GetTimeLTRT(middle, refLtRt);
            if (time >= refLtRt.lt) {
                from = middle;
            }
            else {
                to = middle;
            }
        }
        // Анализ результата
        if (to - from === 0) {
            this.GetTimeLTRT(from, refLtRt);
            if (!(time < refLtRt.lt) && time < refLtRt.rt) {
                return from;
            }
            else if (from > 0) {
                return from - 1;
            }
            else {
                return -1;
            }
        }
        else if (to - from === 1) {
            this.GetTimeLTRT(from, refLtRt);
            const refLtRt1 = { lt: 0, rt: 0 };
            this.GetTimeLTRT(to, refLtRt1);
            if (time >= refLtRt.lt && time < refLtRt.rt) {
                return from;
            }
            else if (time >= refLtRt1.lt && time <= refLtRt1.rt) {
                return to;
            }
            else if (time > refLtRt.lt && time < refLtRt1.lt) {
                // возможно между интервалами дырка, тогда предположим, что дырка - продлжжение левого интервала
                return from;
            }
            else if (this._cashItem != null &&
                this._cashItem.FPeriod < Periods.TIC &&
                to === this._cashItem.Count() - 1) {
                return to;
            }
            else if (time > refLtRt.rt) {
                return -1;
            }
            else if (from > 0) {
                return from - 1;
            }
            else {
                return (time - this.GetValue(0, BarIndex.Time)) / this._TicksInOneBar;
            }
        }
        return -1;
    }
    /// <summary>
    /// Поиск индекса по блокам - оптимизировано для линейных шкал
    /// </summary>
    FindBlockBinary(time, settings) {
        // PointConverterSettings
        if (this.dataBlocks.length === 0) {
            return 0;
        }
        let from = 0;
        let to = this.dataBlocks.length - 1;
        // Бинарный поиск
        while (to - from > 1) {
            const middle = Math.floor(from + (to - from) / 2);
            const leftTime = this.dataBlocks[middle].LeftTime;
            if (!(time < leftTime)) {
                from = middle;
            }
            else {
                to = middle;
            }
        }
        // берем блок
        let db = null;
        if (to === from) {
            db = this.dataBlocks[to];
        }
        else if (to - from === 1) {
            const rightDb = this.dataBlocks[to];
            db = rightDb.LeftTime <= time ? rightDb : this.dataBlocks[from];
        }
        if (db.IsHole) {
            return db.LeftIndex;
        }
        else {
            return db.LeftIndex + (time - db.LeftTime) / this.RelativePeriodBar;
        }
    }
    // длина бара для расчетов времени в будущем и в прошлом
    GetBarDuration(cashItem) {
        // не влияет на движение тулзовин.
        // влият на шкалу времен и на получение времени, например, функциями рисования .нет индикаторов
        const period = cashItem.FPeriod;
        // 1 тик и N тиков - 1 секунда
        if (period <= Periods.TIC) {
            return TimeSpanPeriods.TicksPerSecond;
        }
        // рейндж - 1сек
        if (period % Periods.RANGE === 0) {
            return TimeSpanPeriods.TicksPerSecond;
        }
        // фиксированная длина бара
        return BaseInterval.GetIntervalLength(period);
    }
    /// // <summary>
    /// // Вычисление индекса бара (в т.ч. дробного) для указанного времени
    /// // </summary>
    FindIntervalExactly(time) {
        // look into the past
        if (this.leftPaddingBarsCount > 0 && time < this.RealLeftRelativeBorderForPaddingBars) {
            const e = (time - this.LeftRelativeBorder) / this._BarDuration;
            return e;
        }
        // look into the future...
        if (this.rightPaddingBarsCount > 0 && time >= this.RealRightRelativeBorderForPaddingBars) {
            const count = this.Count();
            const e = count + (time - this.RightRelativeBorder) / this._BarDuration;
            return e;
        }
        // Ticks
        if (this._cashItem != null && this._cashItem.FPeriod === 0) {
            const i = this.FindIntervalBinary(time);
            const curTime = this.GetValue(i, BarIndex.Time);
            const nextTime = this.GetValue(i + 1, BarIndex.Time);
            if (nextTime === curTime) {
                // если 2 тика имеют одно и то же время (бывает на смарт-роуте иногда)
                return i;
            }
            const left = time - curTime;
            return i + left / (nextTime - curTime);
        }
        else if (this.NonLinearTimeline) {
            // Non linear
            const i = this.FindIntervalBinary(time);
            const openBarTime = this.GetValue(i, BarIndex.Time);
            const closeBarTime = this.GetValue(i, BarIndex.TimeClose);
            const left = time - openBarTime;
            return i + left / (closeBarTime - openBarTime);
        }
        else {
            // Linear timeline
            return this.FindBlockBinary(time, this.settings);
        }
    }
    /// // <summary>
    /// // Вычисление времени по указанному индексу (в т.ч. дробному)
    /// // </summary>
    FindTimeExactly(barIndex) {
        let mlt = this._BarDuration;
        const count = this._cashItem != null && this._cashItem.FPeriod === Periods.TIC ? this.Count() - 1 : this.Count();
        // Look into the past...
        if (barIndex < this.leftPaddingBarsCount) {
            const delta = this.LeftRelativeBorder + barIndex * mlt;
            return delta;
        }
        else if (barIndex >= count - this.rightPaddingBarsCount) {
            // Look into the future...
            const delta = barIndex - count;
            return delta * mlt + this.RightRelativeBorder;
        }
        else {
            // inside the cashitem
            const iVal = Math.floor(barIndex);
            const openTime = this.GetValue(iVal, BarIndex.Time);
            if (this._cashItem != null && this._cashItem.FPeriod === 0) {
                // для тиков берем расстояние между барами
                mlt = this.GetValue(iVal + 1, BarIndex.Time) - openTime;
            }
            else if (this.NonLinearTimeline) {
                // для нелинейной шкалы длина бара = close-open
                mlt = this.GetValue(iVal, BarIndex.TimeClose) - openTime;
            }
            return openTime + (barIndex - iVal) * mlt;
        }
    }
    setLeftPaddingBarsCount(leftPaddingCount) {
        if (this.leftPaddingBarsCount === leftPaddingCount) {
            return;
        }
        this.leftPaddingBarsCount = leftPaddingCount;
        this.RecalcIndices();
    }
    setRightPaddingBarsCount(rightPaddingCount) {
        if (this.rightPaddingBarsCount === rightPaddingCount) {
            return;
        }
        this.rightPaddingBarsCount = rightPaddingCount;
        this.RecalcIndices();
    }
    static GetMaxZeroBars(FCashItem) {
        if (FCashItem == null) {
            return 0;
        }
        const period = FCashItem.FPeriod;
        if (period > Periods.TIC) {
            if (period < Periods.DAY) {
                // пустиых баров не более 12 часов
                return (24 * Periods.HOUR) / period;
            }
            else if (period < Periods.MONTH) {
                // день и неделя - макс длина дыры = 1 месяц
                return Periods.MONTH / period;
            }
            else {
                // для месяцев - максимальная дыра = 1 год
                return 12;
            }
        }
        else {
            // Для тик-интервальных и тиковых баров дырок не бывает
            return 0;
        }
    }
}
export const FAKE_LEFT_PADDING_BAR_INDEX = -100; // -1 is busy as invalid index
export const FAKE_RIGHT_PADDING_BAR_INDEX = -101;
export class TerceraChartCashItemSeriesCacheScreenData {
    constructor() {
        this.DateTimeInSessionTimeZoneWithOffset = new Date();
        this.Hole = false;
        this.NotShowExtendedSession = false;
        this.isDayHigh = false;
        this.isDayLow = false;
        /// <summary>
        /// true by default
        /// </summary>
        this.IsMainSession = true;
        this.Separator = false;
        this.DateChangeType = TerceraChartCashItemSeriesCacheScreenDataDateChangeType.None;
    }
}
// #endregion
// #region TerceraChartCashItemSeriesCacheScreenDataStorage
class TerceraChartCashItemSeriesCacheScreenDataStorage {
    constructor() {
        // List<TerceraChartCashItemSeriesCacheScreenData>
        this.MinLow = Number.MAX_VALUE;
        this.MaxHigh = -Number.MAX_VALUE;
        this.MinDayLowIndex = null;
        this.MaxDayHighIndex = null;
        this.Storage = [];
    }
}
/// <summary>
/// Cache some additional info
/// </summary>
// #endregion
// #region TerceraChartCashItemSeriesDataBlock
class TerceraChartCashItemSeriesDataBlock {
    constructor(index, time) {
        this.LeftIndex = index;
        this.LeftTime = time;
        this.IsHole = false;
    }
}
// #endregion
